import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, from, Observable, of, switchMap } from 'rxjs';
import { API_BASE_URL } from '@/app/core/http/api-base-url.token';
import { ApiEnvelope, unwrapEnvelope } from '@/app/core/http/api-envelope.model';
import { AuthHttpClient } from '@/app/core/http/auth-http-client';
import { rethrowWithParsedBody } from '@/app/core/http/blob-error-response.util';
import { filenameFromContentDisposition } from '@/app/shared/utils/content-disposition.util';
import {
  StaffDownloadFormat,
  StaffDownloadJob,
  StaffDownloadResult,
  StaffVigenteDownloadRequest,
} from '../models/staff-vigente-download.model';
import { StaffVigenteDownloadRepository } from './staff-vigente-download.repository';
import { toApiFilterParams } from './staff-vigente.mapper';

const DOWNLOAD_PATH = '/staff/vigente/descarga';
const DEFAULT_FILE_BASE_NAME = 'staff-vigente';

@Injectable()
export class StaffVigenteDownloadHttpRepository extends StaffVigenteDownloadRepository {
  private readonly http = inject(AuthHttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  override download(request: StaffVigenteDownloadRequest): Observable<StaffDownloadResult> {
    const { format, ...criteria } = request;
    return this.http
      .getBlob(`${this.baseUrl}${DOWNLOAD_PATH}`, {
        params: { ...toApiFilterParams(criteria), formato: format },
      })
      .pipe(
        switchMap((response) =>
          response.status === HttpStatusCode.Accepted
            ? from(toDownloadJob(response))
            : of(toDownloadFile(response, format)),
        ),
        catchError((cause: unknown) => from(rethrowWithParsedBody(cause))),
      );
  }
}

function toDownloadFile(
  response: HttpResponse<Blob>,
  format: StaffDownloadFormat,
): StaffDownloadResult {
  if (!response.body) throw new Error('Staff download response has no body.');
  return {
    mode: 'sync',
    file: {
      blob: response.body,
      fileName:
        filenameFromContentDisposition(response.headers.get('Content-Disposition')) ??
        `${DEFAULT_FILE_BASE_NAME}.${format}`,
    },
  };
}

async function toDownloadJob(response: HttpResponse<Blob>): Promise<StaffDownloadResult> {
  const text = (await response.body?.text()) ?? '{}';
  const job = unwrapEnvelope(JSON.parse(text) as ApiEnvelope<StaffDownloadJob>, DOWNLOAD_PATH);
  return { mode: 'async', job };
}
